/**
 * SharedClientOfferContent
 *
 * Presentational component shared between the authenticated ClientOfferPage,
 * the anonymous PublicOfferPage, and the internal read-only version-preview
 * screen (OfferVersionSnapshotView). Resolves the shared banner state and
 * forwards everything else to ClientOfferReviewShell — client-offer-review-v2
 * is the only client offer view (the legacy ClientOfferView + its feature
 * flag were retired once v2 reached parity).
 *
 * Receives all data as props — no query hooks, no useRouteContext.
 * Safe to render on public routes (no AppShell dependency).
 *
 * Traceability: public-offer-client-link / Requirement: Public Offer Page
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AcceptVersionResponse,
  ClientDiffItem,
  ClientOfferViewUnit,
  OfferSnapshotUnit,
  PublicOfferViewUnit,
} from '@ooh/contracts';
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import type { ClientOfferReviewGranularity } from '../client-offer-review';
import { ClientOfferReviewShell } from './client-offer-review-shell';
import { buildClientOfferReviewLabels } from './lib/client-offer-review-labels';
import type { OffersT } from './client-offer-view-labels';

export type OpenClientUnitDetail = (
  mediaUnitId: number,
  options?: { fromVersionId?: string },
) => void;

export interface SharedClientOfferContentProps {
  versionNumber: number;
  publishedAt: Date;
  acceptedAt: Date | null;
  /** Set when the client rejected this version. Drives the rejected banner state. */
  rejectedAt?: Date | null;
  diff: ClientDiffItem[] | null;
  onAccept: () => void;
  /** Read-only mutation state — never calls mutate() from within this component. */
  acceptMutation: Pick<
    { isPending: boolean; isSuccess: boolean; isError: boolean; error: unknown },
    'isPending' | 'isSuccess' | 'isError' | 'error'
  > & { data?: AcceptVersionResponse };
  clientName: string;
  offerTitle: string;
  readOnly?: boolean;
  /**
   * Enables the read-only version-preview mode used by the offer-version
   * snapshot screen (`/offers/:offerId/versions/:versionId`). Every
   * interactive control — per-unit decision buttons, period picker, and the
   * accept footer — stays visible but renders disabled with `previewHint` as
   * a hover tooltip, instead of being hidden. No mutation hooks are wired:
   * pass no-op stubs for `onAccept`/`acceptMutation`.
   * Forces `readOnly` to true regardless of the prop of the same name.
   */
  previewMode?: boolean;
  /** Translated tooltip text shown on hover over any disabled preview control. Required when previewMode is true. */
  previewHint?: string;
  /**
   * Translated tooltip text shown on hover over any disabled control when `readOnly` is
   * true WITHOUT `previewMode` (client-offer-interactive-view / Post-Acceptance Read-Only
   * Client View) — a finalized record shown to the client who created it, distinct from
   * preview mode's "podgląd" framing for an internal viewer. Ignored when `previewMode` is
   * true (previewHint wins) or when `readOnly` is false.
   */
  finalizedHint?: string;
  /**
   * Optional banner rendered directly below the header, above the status
   * banner and content. Used by the version-preview screen for its amber
   * "archival view" notice.
   */
  topBanner?: React.ReactNode;
  /**
   * Offer validity deadline from ClientOfferViewResponseSchema.
   * When present, drives the banner state (sent/expired/accepted).
   * Omitted on the public offer page (no authenticated client-view call there).
   */
  validUntil?: Date;
  /** Per-unit decision state managed by the parent (authenticated view only). */
  decisions?: Record<string, ClientDecision>;
  /** Callback when the client accepts/rejects a unit (authenticated view only). */
  onDecisionChange?: (itemId: string, decision: ClientDecision) => void;
  /** Per-unit selected period ids (authenticated view only). */
  selectedPeriods?: Record<string, string[]>;
  /** Called when client toggles a period klocek (authenticated view only). */
  onPeriodSelect?: (itemId: string, periodId: string, nowSelected: boolean) => void;
  /** Raw snapshot units. */
  snapshotUnits: OfferSnapshotUnit[];
  /** Enriched unit lookup keyed by snapshot unit id — see map-client-view-to-review-unit.ts. */
  enrichedUnitsById?: Map<number, ClientOfferViewUnit | PublicOfferViewUnit>;
  /**
   * True only for the internal version-preview screen when the server's
   * canViewSupplier(user, {}) check passed (Decision 6) — supplier/supplierNumber
   * are still `null` from the server otherwise, so a `true` prop alone shows nothing.
   */
  showSupplierIdentity?: boolean;
  /** Offer-level campaign range — shell's summary stats AND the shared period axis. */
  campaignFrom?: Date | null;
  campaignTo?: Date | null;
  /** SSO bar / access-mode label for the header. Omitted (defaults to public) on the anonymous route. */
  accessMode?: 'Zalogowany (SSO)' | 'Link publiczny';
  loggedInEmail?: string;
  /** Present only on the SSO route (task 3.7) — the public route has no period-inquiry capability. */
  periodInquiry?: { onSubmit: () => void; isPending: boolean };
  /** Opens the client media-unit detail route for a unit's `mediaUnitId`; the app owns the router. */
  onOpenUnitDetail?: (mediaUnitId: number) => void;
  /**
   * Row ids whose accept state and periods are locked (offer-decision-carry-forward /
   * Locked carry-forward entries are enforced on finalize) — sourced from
   * `useClientOfferSelections`'s `lockedRowIds`.
   */
  lockedRowIds?: Set<string>;
  /**
   * Controlled granularity, sourced from the interactive SSO/public routes'
   * useClientOfferSelections so the displayed period axis and the accept/
   * period-select default-period axis can never diverge. Omitted on the
   * read-only version-preview route (no selection state to keep in sync
   * with) — the shell then manages its own local granularity instead.
   */
  granularity?: ClientOfferReviewGranularity;
  onGranularityChange?: (granularity: ClientOfferReviewGranularity) => void;
  /**
   * True only for the internal read-only version-preview screen
   * (OfferVersionSnapshotView) — forwarded to ClientOfferReviewShell's prop
   * of the same name. A historical version snapshot must show the
   * width/height that were true when it was published, not a unit's
   * current live size, which may have changed since.
   */
  frozenDimensions?: boolean;
}

/**
 * Shared inert `onAccept`/`acceptMutation` stub for every read-only rendering of
 * SharedClientOfferContent (post-acceptance read-only view on both the SSO and public
 * routes) — there is nothing to accept and no mutation to wire.
 */
export function noopAccept(): void {}
export const READ_ONLY_ACCEPT_MUTATION: SharedClientOfferContentProps['acceptMutation'] = {
  isPending: false,
  isSuccess: false,
  isError: false,
  error: undefined,
};

export function computeBannerState(
  validUntil: Date,
  acceptedAt: Date | null,
  rejectedAt: Date | null = null,
): 'sent' | 'expired' | 'accepted' | 'rejected' {
  if (rejectedAt != null) return 'rejected';
  if (acceptedAt != null) return 'accepted';
  if (validUntil.getTime() < Date.now()) return 'expired';
  return 'sent';
}

/** Sentinel far-future date used by resolveBannerState so an absent validUntil never
 * trips computeBannerState's `validUntil.getTime() < Date.now()` expired check. */
const FAR_FUTURE_DATE = new Date('9999-12-31');

/**
 * Resolves the banner state when `validUntil` may be absent (public offer page has
 * no authenticated client-view call). Delegates to computeBannerState so the
 * rejected > accepted > sent/expired precedence lives in a single place.
 */
function resolveBannerState(
  validUntil: Date | undefined,
  acceptedAt: Date | null,
  rejectedAt: Date | null,
): 'sent' | 'expired' | 'accepted' | 'rejected' {
  return computeBannerState(validUntil ?? FAR_FUTURE_DATE, acceptedAt, rejectedAt);
}

interface EffectivePreviewProps {
  readOnly: boolean;
  decisionsDisabledHint: string | undefined;
}

/**
 * Resolves the effective readOnly/decisionsDisabledHint values. previewMode always wins:
 * it forces readOnly and surfaces previewHint as the decision-controls tooltip. A plain
 * `readOnly=true` (no previewMode) — the post-acceptance finalized view — surfaces
 * `finalizedHint` instead, never previewHint's "podgląd" framing.
 */
function resolveEffectivePreviewProps(
  previewMode: boolean,
  readOnly: boolean,
  previewHint: string | undefined,
  finalizedHint: string | undefined,
): EffectivePreviewProps {
  if (previewMode) {
    return { readOnly: true, decisionsDisabledHint: previewHint };
  }
  if (readOnly) {
    return { readOnly: true, decisionsDisabledHint: finalizedHint };
  }
  return { readOnly: false, decisionsDisabledHint: undefined };
}

export function SharedClientOfferContent(props: SharedClientOfferContentProps) {
  const {
    acceptedAt,
    rejectedAt = null,
    readOnly = true,
    previewMode = false,
    previewHint,
    finalizedHint,
    validUntil,
  } = props;
  const { t } = useTranslation<'offers'>('offers');

  const bannerState = resolveBannerState(validUntil, acceptedAt, rejectedAt);
  const { readOnly: effectiveReadOnly, decisionsDisabledHint } = resolveEffectivePreviewProps(
    previewMode,
    readOnly,
    previewHint,
    finalizedHint,
  );

  return renderClientOfferReviewShell(
    props,
    t,
    bannerState,
    effectiveReadOnly,
    decisionsDisabledHint,
  );
}

/**
 * Renders the client-offer-review-v2 shell — extracted so SharedClientOfferContent
 * stays a single call, keeping that function's cyclomatic complexity within the lint
 * budget.
 */
// eslint-disable-next-line complexity -- prop-forwarding shim with per-field `?? default` fallbacks, no real branching
function renderClientOfferReviewShell(
  props: SharedClientOfferContentProps,
  t: OffersT,
  bannerState: 'sent' | 'expired' | 'accepted' | 'rejected',
  effectiveReadOnly: boolean,
  decisionsDisabledHint: string | undefined,
) {
  return (
    <ClientOfferReviewShell
      snapshotUnits={props.snapshotUnits}
      enrichedUnitsById={props.enrichedUnitsById}
      showSupplierIdentity={props.showSupplierIdentity ?? false}
      clientName={props.clientName}
      offerTitle={props.offerTitle}
      versionNumber={props.versionNumber}
      publishedAt={props.publishedAt}
      campaignFrom={props.campaignFrom ?? null}
      campaignTo={props.campaignTo ?? null}
      accessMode={props.accessMode ?? 'Link publiczny'}
      loggedInEmail={props.loggedInEmail ?? ''}
      bannerState={bannerState}
      isPreview={props.previewMode ?? false}
      decisions={props.decisions ?? {}}
      onDecisionChange={props.onDecisionChange ?? (() => {})}
      selectedPeriods={props.selectedPeriods ?? {}}
      onPeriodSelect={props.onPeriodSelect ?? (() => {})}
      readOnly={effectiveReadOnly}
      roHint={decisionsDisabledHint ?? ''}
      onAccept={props.onAccept}
      diff={props.diff}
      prevVersion={props.versionNumber - 1}
      labels={buildClientOfferReviewLabels(t)}
      periodInquiry={props.periodInquiry}
      onOpenUnitDetail={props.onOpenUnitDetail}
      lockedRowIds={props.lockedRowIds ?? new Set()}
      topBanner={props.topBanner}
      granularity={props.granularity}
      onGranularityChange={props.onGranularityChange}
      frozenDimensions={props.frozenDimensions}
    />
  );
}
